import React, { useState } from "react";
import { Tag, Button, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faCheck } from "@fortawesome/free-solid-svg-icons";
import type { ColumnsType } from "antd/es/table";
import { EndpointMessageInterface } from "../../../interfaces/EndpointMessage.interface";
import { formatForClipboard } from "./endpointMessageUtils";

function methodColor(method: string): string {
  switch (method) {
    case "GET":
      return "blue";
    case "POST":
      return "green";
    case "PUT":
      return "orange";
    case "PATCH":
      return "gold";
    case "DELETE":
      return "red";
    default:
      return "default";
  }
}

function statusColor(status: number): string {
  if (status >= 200 && status < 300) return "green";
  if (status >= 300 && status < 400) return "cyan";
  if (status >= 400 && status < 500) return "orange";
  if (status >= 500) return "red";
  return "default";
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  return `${d.getHours().toString().padStart(2, "0")}:${d
    .getMinutes()
    .toString()
    .padStart(2, "0")}:${d.getSeconds().toString().padStart(2, "0")}.${d
    .getMilliseconds()
    .toString()
    .padStart(3, "0")}`;
}

function shortenUrl(url: string): string {
  const idx = url.indexOf("/api/");
  return idx >= 0 ? url.slice(idx) : url;
}

function CopyButton({ record }: { record: EndpointMessageInterface }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(formatForClipboard(record)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Tooltip title={copied ? "Copied!" : "Copy"}>
      <Button
        size="small"
        type="text"
        icon={<FontAwesomeIcon icon={copied ? faCheck : faCopy} />}
        onClick={handleCopy}
        style={{ color: copied ? "#52c41a" : undefined, fontSize: 11 }}
      />
    </Tooltip>
  );
}

export default function TerminalEndpointColumns(): ColumnsType<EndpointMessageInterface> {
  return [
    {
      title: "Time",
      key: "timestamp",
      width: 100,
      render: (_: unknown, record: EndpointMessageInterface) => (
        <span style={{ fontFamily: "monospace", fontSize: 11 }}>
          {formatTime(record.timestamp)}
        </span>
      ),
    },
    {
      title: "Method",
      key: "method",
      width: 70,
      render: (_: unknown, record: EndpointMessageInterface) => (
        <Tag color={methodColor(record.method)} style={{ fontSize: 11 }}>
          {record.method}
        </Tag>
      ),
    },
    {
      title: "URL",
      key: "url",
      ellipsis: true,
      render: (_: unknown, record: EndpointMessageInterface) => (
        <span style={{ fontFamily: "monospace", fontSize: 11 }}>{shortenUrl(record.url)}</span>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 70,
      render: (_: unknown, record: EndpointMessageInterface) => (
        <Tag color={statusColor(record.status)}>{record.status || "—"}</Tag>
      ),
    },
    {
      title: "Duration",
      key: "duration",
      width: 80,
      render: (_: unknown, record: EndpointMessageInterface) => (
        <span style={{ fontFamily: "monospace", fontSize: 11 }}>{record.duration}ms</span>
      ),
    },
    {
      title: "Type",
      key: "typeName",
      width: 180,
      ellipsis: true,
      render: (_: unknown, record: EndpointMessageInterface) => {
        if (record.typeName) {
          return <Tag color={record.isFlatBuffer ? "purple" : "default"}>{record.typeName}</Tag>;
        }
        return (
          <Tag color={record.isFlatBuffer ? "volcano" : "default"}>
            {record.isFlatBuffer ? "unknown" : "JSON"}
          </Tag>
        );
      },
    },
    {
      title: "Error",
      key: "error",
      width: 60,
      render: (_: unknown, record: EndpointMessageInterface) =>
        record.error ? (
          <Tag color="red" style={{ fontSize: 10 }}>
            ERR
          </Tag>
        ) : null,
    },
    {
      title: "",
      key: "copy",
      width: 40,
      render: (_: unknown, record: EndpointMessageInterface) => <CopyButton record={record} />,
    },
  ];
}
